import React, { useMemo, useState } from 'react'
import clsx from 'clsx'
import i18n from '@pureartisan/simple-i18n'
import { makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import {
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Customized,
  Tooltip,
} from 'recharts'
import shallow from 'zustand/shallow'

import useStore from './../store'
import {
  Box,
  Typography,
  useTheme,
} from '@material-ui/core'
import { getLevelArray } from '../utils'

const barGap = 2

/**
 * @param {Number} shift Difference between index of a bar in the activeBars array
 * and the orderedBars array. Used to shift each bar to the correct position.
 *
 * NOTE: hack to fix recharts limitation that you can only animate in the furthest
 * right bar, even if the new one is in the middle. So we do technically animate
 * in the last bar, but all bars are shifted w/CSS to keep them in the desired order.
 * @see https://github.com/recharts/recharts/issues/1997
 */
const getTransform = (shift = 0) => ({
  transform: `translateX(calc(${shift}*(100% + ${barGap}px)))`,
})

const useStyles = makeStyles(theme => ({
  root: {
    '& .recharts-yAxis text': {
      ...theme.typography.caption,
      color: theme.extras.variables.colors.textSubtle,
      transform: 'translateX(5px)',
    },
    '& .recharts-tooltip-wrapper .MuiTypography-body1': {
      color: theme.extras.variables.colors.white,
    },
  },
  tooltip: {
    background: theme.extras.variables.colors.black,
    color: theme.extras.variables.colors.white,
    borderRadius: '4px',
    padding: theme.spacing(1),
    '& .MuiTypography-root': {
      fontSize: '13px',
      fontFamily: `'Fira Sans', helvetica, arial`,
      lineHeight: '1.4em',
    },
  },
  levelsText: {
    ...theme.typography.caption,
    color: theme.extras.variables.colors.textSubtle,
  },
  levelsBackground: {
    '&.vl': { fill: theme.extras.ratings.levelColors[0] },
    '&.l': { fill: theme.extras.ratings.levelColors[1] },
    '&.m': { fill: theme.extras.ratings.levelColors[2] },
    '&.h': { fill: theme.extras.ratings.levelColors[3] },
    '&.vh': { fill: theme.extras.ratings.levelColors[4] },
  },
  barChart: {
    '& .recharts-bar-rectangle path': {
      transformBox: 'fill-box',
      transformOrigin: '0 0',
    },
  },
  // bar colors, transforms
  ai: ({ ai }) => ({
    fill: theme.extras.demos.ai,
    '& .recharts-bar-rectangle path': getTransform(ai),
  }),
  ap: ({ ap }) => ({
    fill: theme.extras.demos.ap,
    '& .recharts-bar-rectangle path': getTransform(ap),
  }),
  b: ({ b }) => ({
    fill: theme.extras.demos.b,
    '& .recharts-bar-rectangle path': getTransform(b),
  }),
  hi: ({ hi }) => ({
    fill: theme.extras.demos.hi,
    '& .recharts-bar-rectangle path': getTransform(hi),
  }),
  w: ({ w }) => ({
    fill: theme.extras.demos.w,
    '& .recharts-bar-rectangle path': getTransform(w),
  }),
  t: ({ t = 0 }) => ({
    fill: theme.extras.demos.t,
    '& .recharts-bar-rectangle path': getTransform(t),
  }),
}))

const Chart = ({ data, barOptions, activeBars }) => {
  const theme = useTheme()
  const bgWidth = theme.extras.chart.width - 57 // TODO why 57?
  const bgHeight = 280 // TODO: move out of chart, combine with sdsPanel labels below chart
  const xOffset = 25

  const { activeYear, activeNorm, activeRegion } = useStore(
    state => ({
      activeYear: state.activeYear,
      activeNorm: state.activeNorm,
      activeRegion: state.activeRegion,
    }),
    shallow,
  )

  const levelArray = getLevelArray()

  const [hovered, setHovered] = useState(null)

  const renderTooltip = ({ active, payload }) => {
    if (!active || !payload || !hovered) return null

    const { demo, lvlIdx } = hovered

    const tooltipData = payload.find(
      ({ dataKey }) => dataKey === demo,
    )
    const { value, dataKey } = tooltipData || {}
    if (!value) return null

    const group = i18n.translate(
      `POP_${String(dataKey).toUpperCase()}`,
    )
    const children = i18n.translate(`CHILDREN`)
    return (
      <Box className={classes.tooltip}>
        <Typography variant="body1">
          {group} {children}, {levelArray[lvlIdx]}:
        </Typography>
        <Typography variant="body1">
          {Math.round(value) + '%'}
        </Typography>
      </Box>
    )
  }

  const Background = () => {
    const yOffset = 14
    const indexes = ['vl', 'l', 'm', 'h', 'vh']

    return (
      <>
        <rect
          style={{ fill: '#fff' }}
          x={xOffset}
          y={yOffset - 2}
          height={bgHeight + 4}
          width={bgWidth + 4}
        ></rect>
        {indexes.map((val, i) => {
          const xPos =
            (i * bgWidth) / indexes.length + xOffset
          return (
            <line
              key={i}
              x1={xPos}
              x2={xPos}
              y1={yOffset}
              y2={yOffset + bgHeight}
              stroke="#EDEDED"
            />
          )
        })}
        <line
          x1={bgWidth + xOffset}
          x2={bgWidth + xOffset}
          y1={yOffset}
          y2={yOffset + bgHeight}
          stroke="#EDEDED"
        />
      </>
    )
  }
  const Levels = () => {
    // const yOffset = 10
    const indexes = ['vl', 'l', 'm', 'h', 'vh']

    // return <rect x={4} y={10} width={20} />

    const rectWidth = bgWidth / indexes.length
    return (
      <>
        {indexes.map((val, i) => {
          const x =
            (i * bgWidth) / indexes.length + xOffset + 1
          return (
            <React.Fragment key={val}>
              <rect
                className={clsx(
                  classes.levelsBackground,
                  val,
                )}
                x={
                  (i * bgWidth) / indexes.length +
                  xOffset +
                  1
                }
                y={bgHeight + 20}
                rx={3}
                ry={3}
                height={8}
                width={rectWidth - 2}
              ></rect>
              <text
                className={classes.levelsText}
                x={x + rectWidth / 2}
                y={bgHeight + 37}
                dominantBaseline="middle"
                textAnchor="middle"
                fontSize={10}
              >
                {levelArray[i]}
              </text>
            </React.Fragment>
          )
        })}
      </>
    )
  }

  /**
   * Finds the highest value from the arrays of data for
   * active demographics.
   */
  const getMaxValue = useMemo(() => {
    let maxValue = 0
    activeBars.forEach(el => {
      data.forEach(row => {
        // console.log(`row val for ${row}`, row[el], maxValue)
        if (row[el] > maxValue) {
          maxValue = row[el]
        }
      })
    })

    return Math.min(
      Math.ceil((maxValue + 1) / 10) * 10,
      100,
    )
  }, [activeBars, activeYear, activeNorm, activeRegion])

  // console.log('data is, ', data, activeBars)

  const orderedBars = useMemo(
    () =>
      barOptions.filter(
        el =>
          activeBars.includes(el) &&
          // some POI sites have no data for certain demos
          data.some(row => row[el] > 0),
      ),
    [activeBars, data],
  )

  const shiftMap = {}
  activeBars.forEach((demo, idx) => {
    shiftMap[demo] = orderedBars.indexOf(demo) - idx
  })
  // pass in the shift values to be used by getTransform
  const classes = useStyles(shiftMap)

  const addPercent = el => `${el}%`

  return (
    <div
      className={clsx(
        classes.root,
        'responsive-container-parent',
      )}
      style={{
        height: bgHeight + 46.5 + 'px',
        width: '100%',
      }}
    >
      <ResponsiveContainer>
        <BarChart
          className={classes.barChart}
          data={data}
          margin={{
            top: 15,
            right: 0,
            bottom: 0,
            left: 0,
          }}
          barCategoryGap="10%"
          barGap={barGap}
        >
          <CartesianGrid
            horizontal={false}
            vertical={false}
          />
          <XAxis
            display={'none'}
            dataKey="name"
            tickLine={false}
            axisLine={true}
          />
          <YAxis
            width={xOffset}
            minTickGap={0}
            axisLine={false}
            tickLine={false}
            domain={[0, getMaxValue]}
            tickCount={getMaxValue / 10 + 1}
            tickFormatter={addPercent}
          />
          <Tooltip
            // animationDuration={100}
            isAnimationActive={false}
            content={renderTooltip}
          />
          <Customized key={'bg'} component={Background} />
          <Customized key={'x'} component={Levels} />
          {!!activeBars
            ? activeBars.map((demo, i) => (
                <Bar
                  onMouseOver={(d, lvlIdx) =>
                    setHovered({ demo, lvlIdx })
                  }
                  onMouseLeave={() => setHovered(null)}
                  key={i}
                  radius={[4, 4, 4, 4]}
                  dataKey={demo}
                  className={classes[demo]}
                />
              ))
            : ''}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

Chart.propTypes = {
  data: PropTypes.array,
  activeBars: PropTypes.array,
}

export default Chart
